import { useIsDemo } from "@/demo/DemoContext";
import { useCallback, useEffect, useState } from "react";
import {
  CheckCircle,
  Copy,
  FileText,
  FilterX,
  GitCompareArrows,
  MoreHorizontal,
  RefreshCw,
  RotateCcw,
  Send,
  Truck,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { daysFromToday, formatCurrency, formatDate } from "@/lib/format";
import { matchStatusLabel, matchStatusVariant, stateLabel, stateVariant } from "@/lib/bill-utils";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
  EmptyState,
  EntityCell,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
  rowLink,
} from "@/components/PageUi";

type BillResponse = components["schemas"]["BillResponse"];
type BillState = components["schemas"]["BillState"];
type BillMatchSummary = components["schemas"]["BillMatchSummary"];
type MatchedDocumentRef = components["schemas"]["MatchedDocumentRef"];

export interface BillsListFilters {
  /** Zero or more bill states to filter by. Empty/undefined = all states. */
  states?: BillState[];
  supplierId?: string;
  invoiceNumber?: string;
  /**
   * Matched ignoring punctuation and case ("po 01750" finds "PO 01750"): nobody typing a PO knows how the
   * supplier punctuated it.
   */
  normalisedBillPoNumber?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  /** Single spend tag — matches bills whose baked tag union contains it. */
  tag?: string;
  /** true = only bills flagged as a possible duplicate; false = only unflagged. Undefined = no filter. */
  duplicate?: boolean;
}

const PAGE_SIZE = 10;

// States in which a due date still means something to act on. Once a bill is paid, voided or rejected, how
// late it "is" stops being a question, so the urgency line goes quiet.
const OPEN_STATES: ReadonlySet<BillState> = new Set<BillState>([
  "PendingReview",
  "NeedsApproval",
  "Approved",
  "Scheduled",
  "PaymentFailed",
  "PartiallyPaid",
]);

// Approved onward a bill owes something (or nothing, once paid); before that, outstanding means nothing yet.
const OWING_STATES: ReadonlySet<BillState> = new Set<BillState>([
  "Approved",
  "Scheduled",
  "PaymentInProgress",
  "PaymentFailed",
  "PartiallyPaid",
  "Paid",
]);


// Overdue is the one reading that earns colour: the only one a person needs to act on today.
function DueCell({ dueDate, state }: { dueDate?: string | null; state?: BillState }) {
  if (!dueDate) return <span className="text-sm text-muted-foreground">-</span>;
  const open = state != null && OPEN_STATES.has(state);
  const days = open ? daysFromToday(dueDate) : null;
  return (
    <div className="leading-tight">
      <div className="text-sm text-muted-foreground">{formatDate(dueDate)}</div>
      {days != null ? (
        <div
          className={cn(
            "text-xs tabular-nums",
            days < 0 ? "font-medium text-red-600 dark:text-red-400" : "text-muted-foreground",
          )}
        >
          {days < 0
            ? `${-days} ${days === -1 ? "day" : "days"} overdue`
            : days === 0
              ? "Due today"
              : days <= 14
                ? `In ${days} ${days === 1 ? "day" : "days"}`
                : null}
        </div>
      ) : null}
    </div>
  );
}

// Extraction confidence pill: red below 50%, amber 50-70%, green 70%+.
function ConfidencePill({ confidence }: { confidence: number | string }) {
  const pct = Math.round(Number(confidence) * 100);
  const className =
    pct < 50
      ? "border-red-500/30 bg-red-500/15 text-red-600"
      : pct < 70
        ? "border-amber-500/30 bg-amber-500/15 text-amber-600"
        : "border-emerald-500/30 bg-emerald-500/15 text-emerald-600";
  return (
    <Badge variant="outline" className={cn("tabular-nums", className)}>
      {pct}%
    </Badge>
  );
}

// Plain text unless the host wired a click-through: not every caller has a PO or delivery-note page.
function MatchedDocLink({
  doc,
  icon: Icon,
  onClick,
}: {
  doc: MatchedDocumentRef;
  icon: typeof FileText;
  onClick?: (id: string) => void;
}) {
  const body = (
    <>
      <Icon className="size-3 shrink-0" />
      <span className="truncate">{doc.number}</span>
    </>
  );

  if (!onClick) {
    return (
      <span className="inline-flex max-w-[150px] items-center gap-1 font-mono text-[10px] text-muted-foreground">
        {body}
      </span>
    );
  }

  return (
    <button
      type="button"
      className="inline-flex max-w-[150px] items-center gap-1 rounded font-mono text-[10px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={(e) => {
        // The whole row navigates to the bill; a document link must not do both.
        e.stopPropagation();
        onClick(doc.id);
      }}
    >
      {body}
    </button>
  );
}

// A null summary (no match group, the normal no-PO case) renders a dash, NOT "Unmatched": that is Open,
// a group with a candidate still to work through.
function MatchCell({
  summary,
  onPurchaseOrderClick,
  onDeliveryNoteClick,
}: {
  summary: BillMatchSummary | null | undefined;
  onPurchaseOrderClick?: (purchaseOrderId: string) => void;
  onDeliveryNoteClick?: (deliveryNoteId: string) => void;
}) {
  if (!summary) {
    return <span className="text-xs text-muted-foreground">-</span>;
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Badge
        variant={matchStatusVariant(summary.status)}
        className="text-[10px] uppercase tracking-wider"
      >
        {matchStatusLabel(summary.status)}
      </Badge>
      {(summary.matchedPurchaseOrders.length > 0 || summary.matchedDeliveryNotes.length > 0) && (
        <div className="flex flex-col items-end gap-0.5">
          {summary.matchedPurchaseOrders.map((po) => (
            <MatchedDocLink key={po.id} doc={po} icon={FileText} onClick={onPurchaseOrderClick} />
          ))}
          {summary.matchedDeliveryNotes.map((dn) => (
            <MatchedDocLink key={dn.id} doc={dn} icon={Truck} onClick={onDeliveryNoteClick} />
          ))}
        </div>
      )}
    </div>
  );
}

export interface BillsTableProps {
  /** Server-side filters. Changing any field re-fetches from the first page. */
  filters?: BillsListFilters;
  /** Hide the Supplier column — used on the supplier detail page where it's redundant. */
  hideSupplierColumn?: boolean;
  onBillClick: (billId: string) => void;
  onMatchClick?: (billId: string) => void;
  /** Off by default: populating `matchSummary` costs two extra lookups per page. */
  includeMatch?: boolean;
  onPurchaseOrderClick?: (purchaseOrderId: string) => void;
  onDeliveryNoteClick?: (deliveryNoteId: string) => void;
  /** Bump this number to force a refetch (e.g. after the host uploads a bill). */
  reloadSignal?: number;
  /** Reports the number of bills currently shown, so the host can render a count. */
  onCountChange?: (count: number) => void;
}

export function BillsTable({
  filters,
  hideSupplierColumn = false,
  onBillClick,
  onMatchClick,
  includeMatch = false,
  onPurchaseOrderClick,
  onDeliveryNoteClick,
  reloadSignal,
  onCountChange,
}: BillsTableProps) {
  const isDemo = useIsDemo();
  const api = useDolfinClient();

  const [bills, setBills] = useState<BillResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  // Submit for review needs reviewer settings and an empty-state dialog, so that row action just opens the bill.
  const [actingBillId, setActingBillId] = useState<string | null>(null);
  const [billToApprove, setBillToApprove] = useState<BillResponse | null>(null);
  const [billToReject, setBillToReject] = useState<BillResponse | null>(null);
  const [billToReopen, setBillToReopen] = useState<BillResponse | null>(null);
  const [billToRetryOcr, setBillToRetryOcr] = useState<BillResponse | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  // Destructure so the fetch callback depends on the primitive filter values
  // rather than the (possibly fresh-every-render) filters object identity.
  const states = filters?.states;
  const supplierId = filters?.supplierId;
  const invoiceNumber = filters?.invoiceNumber;
  const normalisedBillPoNumber = filters?.normalisedBillPoNumber;
  const dueDateFrom = filters?.dueDateFrom;
  const dueDateTo = filters?.dueDateTo;
  const tag = filters?.tag;
  const duplicate = filters?.duplicate;
  const filtersActive =
    (states?.length ?? 0) > 0 ||
    !!supplierId ||
    !!invoiceNumber ||
    !!normalisedBillPoNumber ||
    !!dueDateFrom ||
    !!dueDateTo ||
    !!tag ||
    duplicate !== undefined;

  const fetchBills = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);

      try {
        // IncludeMatch, not the match group: that would re-run the fuzzy suggestion engine once per row.
        const query: Record<string, string | number | string[] | boolean> = {
          Limit: PAGE_SIZE,
          IncludeMatch: includeMatch,
        };
        if (states && states.length > 0) query.States = states;
        if (supplierId) query.SupplierId = supplierId;
        if (invoiceNumber) query.InvoiceNumber = invoiceNumber;
        if (normalisedBillPoNumber) query.NormalisedBillPoNumber = normalisedBillPoNumber;
        if (dueDateFrom) query.DueDateFrom = dueDateFrom;
        if (dueDateTo) query.DueDateTo = dueDateTo;
        if (tag) query.Tag = tag;
        if (duplicate !== undefined) query.Duplicate = duplicate;
        if (cursor) query.Cursor = cursor;

        const { data, error: apiError } = await api.GET("/v1/bills", { params: { query } });

        if (apiError || !data) {
          console.error("Failed to load bills:", apiError);
          setError("Failed to load bills");
          return;
        }

        const rows = data.data ?? [];
        setBills(rows);
        onCountChange?.(rows.length);
        const next = data.meta?.cursors?.next ?? null;
        setNextCursor(next);
        setHasMore(next !== null);
      } catch (e) {
        console.error("Failed to load bills:", e);
        setError("Failed to load bills");
      } finally {
        setLoading(false);
      }
    },
    [
      api,
      states,
      supplierId,
      invoiceNumber,
      normalisedBillPoNumber,
      dueDateFrom,
      dueDateTo,
      tag,
      duplicate,
      includeMatch,
      onCountChange,
    ],
  );

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchBills();
    // reloadSignal is an explicit refetch trigger from the host.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBills, reloadSignal]);

  function handleNextPage() {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    fetchBills(nextCursor);
  }

  function handlePrevPage() {
    const newStack = [...cursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1];
    setCursorStack(newStack);
    fetchBills(prevCursor);
  }

  // Patch in place so the row moves to its new state without a re-fetch flicker.
  function patchBill(updated: BillResponse) {
    if (!updated.id) return;
    setBills((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  }

  async function confirmApprove() {
    const target = billToApprove;
    if (!target?.id) return;
    const id = target.id;
    setBillToApprove(null);
    setActingBillId(id);
    const { data, error: apiError } = await api.POST("/v1/bills/{id}/approve", {
      params: { path: { id } },
    });
    setActingBillId(null);
    if (apiError || !data) {
      toast.error(apiErrorMessage(apiError, "Failed to approve bill"));
      return;
    }
    patchBill(data);
    toast.success("Bill approved");
  }

  async function confirmReject() {
    const target = billToReject;
    if (!target?.id) return;
    const id = target.id;
    const reason = rejectReason.trim() || null;
    setBillToReject(null);
    setRejectReason("");
    setActingBillId(id);
    const { data, error: apiError } = await api.POST("/v1/bills/{id}/reject", {
      params: { path: { id } },
      body: { reason },
    });
    setActingBillId(null);
    if (apiError || !data) {
      toast.error(apiErrorMessage(apiError, "Failed to reject bill"));
      return;
    }
    patchBill(data);
    toast.success("Bill rejected");
  }

  async function confirmReopen() {
    const target = billToReopen;
    if (!target?.id) return;
    const id = target.id;
    setBillToReopen(null);
    setActingBillId(id);
    const { data, error: apiError } = await api.POST("/v1/bills/{id}/reopen", {
      params: { path: { id } },
    });
    setActingBillId(null);
    if (apiError || !data) {
      toast.error(apiErrorMessage(apiError, "Failed to reopen bill"));
      return;
    }
    patchBill(data);
    toast.success("Bill reopened for editing");
  }

  async function confirmRetryOcr() {
    const target = billToRetryOcr;
    if (!target?.id) return;
    const id = target.id;
    setBillToRetryOcr(null);
    setActingBillId(id);
    const { error: apiError } = await api.POST("/v1/bills/{id}/retry-extraction", {
      params: { path: { id } },
    });
    setActingBillId(null);
    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to retry extraction"));
      return;
    }
    // Optimistic: this endpoint doesn't return the new state, but the bill moves to Extracting at once.
    setBills((prev) =>
      prev.map((b) => (b.id === id ? { ...b, state: "Extracting" } : b)),
    );
    toast.success("Extraction retried");
  }

  const currentPage = cursorStack.length + 1;

  if (loading) {
    return <LoadingState label="Loading bills..." />;
  }

  if (error) {
    return (
      <ErrorState
        message="The bills could not be loaded."
        onRetry={() => fetchBills(cursorStack[cursorStack.length - 1])}
      />
    );
  }

  if (bills.length === 0) {
    // Two different situations: nothing matches what was asked for, or there is simply nothing yet. The
    // first is fixed by loosening the filters, the second by getting a bill in.
    return filtersActive ? (
      <EmptyState icon={FilterX} title="No bills match these filters">
        Try widening the dates or clearing a filter.
      </EmptyState>
    ) : (
      <EmptyState icon={FileText} title="No bills yet">
        Upload a PDF or photo of a supplier invoice, or forward it to your bills inbox, and it will appear
        here once it has been read.
      </EmptyState>
    );
  }

  return (
    <>
      <TableShell>
        <Table>
          <TableHeader>
            <HeaderRow>
              {!hideSupplierColumn && (
                <TableHead className={TH}>
                  Supplier
                </TableHead>
              )}
              <TableHead className={TH}>
                Invoice #
              </TableHead>
              <TableHead className={TH}>
                Invoice date
              </TableHead>
              <TableHead className={TH}>
                Due
              </TableHead>
              <TableHead className={cn(TH, "text-right")}>
                Amount
              </TableHead>
              <TableHead className={cn(TH, "text-right")}>
                Outstanding
              </TableHead>
              <TableHead className={cn(TH, "w-[90px] text-center")}>
                Confidence
              </TableHead>
              <TableHead className={cn(TH, "w-[140px] text-right")}>
                State
              </TableHead>
              {includeMatch && (
                <TableHead className={cn(TH, "w-[150px] text-right")}>
                  Match
                </TableHead>
              )}
              <TableHead className="w-[48px]" />
            </HeaderRow>
          </TableHeader>
          <TableBody>
            {bills.map((bill) => {
              const isActing = actingBillId === bill.id;
              const showSubmit = bill.state === "PendingReview";
              const showApproveReject = bill.state === "NeedsApproval";
              const showReopen = bill.state === "Rejected";
              // A demo can't retry: each retry is an uncapped paid extraction.
              const showRetryOcr = !isDemo && (bill.state === "ExtractionFailed" || bill.state === "OcrFailed");
              const showMatch =
                onMatchClick != null &&
                bill.state !== "Extracting" &&
                bill.state !== "OcrProcessing";
              const showMenu =
                showSubmit ||
                showApproveReject ||
                showReopen ||
                showRetryOcr ||
                showMatch;
              return (
                <TableRow key={bill.id} {...rowLink(bill.id ? () => onBillClick(bill.id!) : undefined)}>
                  {!hideSupplierColumn && (
                    <TableCell className="max-w-[260px]">
                      {/* The file name is the second line: until the supplier is confirmed it is often
                          the only thing that tells two bills from the same sender apart. */}
                      <EntityCell
                        name={bill.supplier?.name || bill.supplierName}
                        fallback="Unknown supplier"
                        secondary={bill.fileName}
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-mono text-xs">
                    {bill.invoiceNumber || "-"}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {bill.invoiceDate ? formatDate(bill.invoiceDate) : "-"}
                  </TableCell>
                  <TableCell>
                    <DueCell dueDate={bill.dueDate} state={bill.state} />
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {formatCurrency(bill.totalAmount, bill.currency ?? "GBP")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {bill.state && OWING_STATES.has(bill.state) ? (
                      <span className={cn("text-sm", Number(bill.outstanding ?? 0) === 0 && "text-muted-foreground")}>
                        {formatCurrency(bill.outstanding, bill.currency ?? "GBP")}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {bill.confidence != null ? (
                      <ConfidencePill confidence={bill.confidence} />
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Only while unresolved: once confirmed as a separate invoice the bill keeps its
                          link to the other one, but there is nothing left for the list to warn about. */}
                      {bill.duplicateOfBillId && !bill.duplicateDismissedAt && (
                        <Badge
                          variant="outline"
                          className="gap-1 border-amber-500/30 bg-amber-500/15 text-[10px] uppercase tracking-wider text-amber-600"
                        >
                          <Copy className="size-3" />
                          Duplicate?
                        </Badge>
                      )}
                      <Badge
                        variant={stateVariant(bill.state)}
                        className="text-[10px] uppercase tracking-wider"
                      >
                        {stateLabel(bill.state)}
                      </Badge>
                    </div>
                  </TableCell>
                  {includeMatch && (
                    <TableCell className="text-right">
                      <MatchCell
                        summary={bill.matchSummary}
                        onPurchaseOrderClick={onPurchaseOrderClick}
                        onDeliveryNoteClick={onDeliveryNoteClick}
                      />
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    {showMenu && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={isActing}
                            className="size-8"
                            aria-label="Bill actions"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            {isActing ? (
                              <Spinner className="size-4" />
                            ) : (
                              <MoreHorizontal className="size-4" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-48"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          {showSubmit && bill.id && (
                            <DropdownMenuItem
                              onClick={() => onBillClick(bill.id!)}
                            >
                              <Send className="mr-2 size-4" />
                              Submit for review
                            </DropdownMenuItem>
                          )}
                          {showApproveReject && (
                            <>
                              <DropdownMenuItem onClick={() => setBillToApprove(bill)}>
                                <CheckCircle className="mr-2 size-4" />
                                Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setBillToReject(bill)}
                              >
                                <XCircle className="mr-2 size-4" />
                                Reject
                              </DropdownMenuItem>
                            </>
                          )}
                          {showReopen && (
                            <DropdownMenuItem onClick={() => setBillToReopen(bill)}>
                              <RotateCcw className="mr-2 size-4" />
                              Reopen for editing
                            </DropdownMenuItem>
                          )}
                          {showRetryOcr && (
                            <DropdownMenuItem onClick={() => setBillToRetryOcr(bill)}>
                              <RefreshCw className="mr-2 size-4" />
                              Retry extraction
                            </DropdownMenuItem>
                          )}
                          {showMatch && bill.id && (
                            <DropdownMenuItem onClick={() => onMatchClick!(bill.id!)}>
                              <GitCompareArrows className="mr-2 size-4" />
                              Open bill match
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableShell>

      <Pager
        page={currentPage}
        hasPrevious={currentPage > 1}
        hasNext={hasMore}
        onPrevious={handlePrevPage}
        onNext={handleNextPage}
      />
      <AlertDialog
        open={billToApprove !== null}
        onOpenChange={(open) => !open && setBillToApprove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve this bill?</AlertDialogTitle>
            <AlertDialogDescription>
              {billToApprove?.supplier?.name || billToApprove?.supplierName || "This bill"}
              {" · "}
              {formatCurrency(billToApprove?.totalAmount, billToApprove?.currency ?? "GBP")}
              {" - once approved it moves into the payment flow."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmApprove}>Approve</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={billToReject !== null}
        onOpenChange={(open) => {
          if (!open) {
            setBillToReject(null);
            setRejectReason("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this bill?</AlertDialogTitle>
            <AlertDialogDescription>
              {billToReject?.supplier?.name || billToReject?.supplierName || "This bill"} will be
              moved to Rejected. You can reopen it later for editing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Reason for rejection (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmReject}>
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={billToReopen !== null}
        onOpenChange={(open) => !open && setBillToReopen(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen for editing?</AlertDialogTitle>
            <AlertDialogDescription>
              The bill returns to Pending Review so the fields can be corrected and re-submitted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReopen}>Reopen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={billToRetryOcr !== null}
        onOpenChange={(open) => !open && setBillToRetryOcr(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retry extraction?</AlertDialogTitle>
            <AlertDialogDescription>
              The document will be re-processed. This usually takes a few seconds.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRetryOcr}>Retry</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
